import { Fieldset, Heading, Layout, Paragraph } from '@vwfs-bronson/bronson-react';
import { Form, Formik, FormikProps } from 'formik';
import React, { FunctionComponent, useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { formAutocomplete } from '../../../../config';
import { journeyDataActions } from '../../../../services/redux/features/journeyData.slice';
import { ProspectFormNavigation } from '../ProspectFormNavigation';
import { StoreQuoteTwelveMonthsConsent } from '../StoreQuoteTwelveMonthsConsent';
import { createFormInputs } from './formInputs';
import { getInitialValues } from './initialValues';
import { requestMapper } from './requestMapper';
import { privateProspectFormSchema } from './validationSchema';
import { CustomerSearchModal } from './CustomerSearch/CustomerSearchModal';
import { MockSearchCustomerItem } from './CustomerSearch/formModel';
import { uiStateActions } from '../../../../services/redux/features/uiState.slice';

export const PrivateProspectForm: FunctionComponent = () => {
  const { t } = useTranslation();

  const [initialValues] = useState(getInitialValues);
  const dispatch = useDispatch();
  const formikRef = useRef<FormikProps<any>>(null);

  const handleShowingCustomerSearchModal = useCallback(() => {
    dispatch(uiStateActions.setShownCustomerSearchModal(true));
  }, [dispatch]);

  const inputs = createFormInputs();

  const handleCustomerSelected = (customer: MockSearchCustomerItem | null) => {
    if (!customer) {
      dispatch(uiStateActions.setShownCustomerSearchModal(false));
      return;
    }

    const formik = formikRef.current;
    if (!formik) return;

    formik.setFieldValue('firstName', customer.firstName ?? '');
    formik.setFieldValue('lastName', customer.lastName ?? '');
    formik.setFieldValue('email', customer.email ?? '');
    formik.setFieldValue('countryCode', customer.countryCode ?? '');
    formik.setFieldValue('mobileNumber', customer.mobileNumber ?? '');

    // reset fields validation
    formik.setFieldTouched('firstName', false, false);
    formik.setFieldTouched('lastName', false, false);
    formik.setFieldTouched('email', false, false);
    formik.setFieldTouched('countryCode', false, false);
    formik.setFieldTouched('mobileNumber', false, false);
    formik.setFieldError('firstName', undefined);
    formik.setFieldError('lastName', undefined);
    formik.setFieldError('email', undefined);
    formik.setFieldError('countryCode', undefined);
    formik.setFieldError('mobileNumber', undefined);

    dispatch(uiStateActions.setShownCustomerSearchModal(false));
  };

  return (
    <>
      <Layout center>
        <Heading level={2}>{t('prospectPage:private:title')}</Heading>
      </Layout>
      <Formik
        innerRef={formikRef}
        validationSchema={privateProspectFormSchema()}
        initialValues={initialValues}
        onSubmit={(values, { setSubmitting }) => {
          setSubmitting(false);
          dispatch(journeyDataActions.updateData(requestMapper(values)));
        }}
        validateOnMount
      >
        {() => (
          <>
            <Form autoComplete={formAutocomplete} className="u-text-left">
              <Fieldset.Row>
                <Layout>
                  <Layout.Item default="1/2" s="1/1">
                    {inputs.firstName(handleShowingCustomerSearchModal)}
                  </Layout.Item>
                  <Layout.Item default="1/2" s="1/1">
                    {inputs.lastName()}
                  </Layout.Item>
                </Layout>
              </Fieldset.Row>

              <Fieldset.Row>
                <Layout>
                  <Layout.Item default="1/6" s="1/1">
                    {inputs.countryCode()}
                  </Layout.Item>
                  <Layout.Item default="1/3" s="1/1">
                    {inputs.mobileNumber()}
                  </Layout.Item>
                  <Layout.Item default="1/2" s="1/1">
                    {inputs.email()}
                  </Layout.Item>
                </Layout>
              </Fieldset.Row>

              <Layout.Item>{inputs.vulnerableCustomer()}</Layout.Item>

              <Heading level={5} className="u-mt u-mb-small">
                {t('prospectPage:private:sectionTitle')}
              </Heading>
              <Paragraph>{t('prospectPage:consent:paragraph')}</Paragraph>

              <StoreQuoteTwelveMonthsConsent />
            </Form>
            <CustomerSearchModal onCustomerSelected={handleCustomerSelected} />
            <ProspectFormNavigation />
          </>
        )}
      </Formik>
    </>
  );
};
